import { useCallback } from 'react';
import { COMMON_MOBILE_BREAKPOINT, Show } from '@elfsight/lib-utils';
import {
  ApplyFiltersButton,
  ClearFiltersButton,
  FiltersGroup,
  FiltersModal,
  formatCount,
  InlineFilters,
  ModalTriggerButton,
  useFilters
} from '@elfsight/widget-filters';
import { useTranslation } from 'react-i18next';
import { MIN_WIDTH_FOR_FILTERS_STYLE } from '../../../constants';
import { useLayout } from '../../layout';
import { FiltersList } from '../components';
import type { FilterOptions } from '../types';
import { useJobFilters } from '../utils';

type ModalFiltersViewProps = {
  filterOptions: FilterOptions;
};

export function ModalFiltersView({ filterOptions }: ModalFiltersViewProps) {
  const { t } = useTranslation();
  const { widgetContainerWidth } = useLayout();
  const { filtersAppliedCount, resetFilters, clearSelectedFilters } =
    useFilters();
  const { processedJobs, activeFilters } = useJobFilters();

  const isCompactMode = widgetContainerWidth < MIN_WIDTH_FOR_FILTERS_STYLE;
  const shouldShowClearButton = !!filtersAppliedCount && !isCompactMode;

  const triggerButtonText =
    widgetContainerWidth >= COMMON_MOBILE_BREAKPOINT ? t('Filters') : undefined;

  const selectedJobsCount = processedJobs.length;

  const renderApplyText = useCallback(
    ({
      selectedJobsCount,
      formattedCount
    }: {
      selectedJobsCount: number;
      formattedCount: string;
    }) => {
      if (selectedJobsCount === 1) {
        return t('See 1 Job');
      }

      if (selectedJobsCount > 1) {
        return t('See {{jobCount}} Jobs', {
          jobCount: formattedCount
        });
      }

      return t('No Results');
    },
    [t]
  );

  const applyText = renderApplyText({
    selectedJobsCount,
    formattedCount: formatCount(selectedJobsCount)
  });

  return (
    <InlineFilters>
      <FiltersModal
        type="popup"
        trigger={<ModalTriggerButton>{triggerButtonText}</ModalTriggerButton>}
        title={t('Filters')}
        clearButton={
          <ClearFiltersButton>{t('Clear Filters')}</ClearFiltersButton>
        }
        applyButton={
          <ApplyFiltersButton count={selectedJobsCount}>
            {applyText}
          </ApplyFiltersButton>
        }
        onClearRequest={clearSelectedFilters}
      >
        {activeFilters.map((filter) => (
          <FiltersGroup key={filter.id} title={filter.label}>
            <FiltersList
              activeFilters={[filter]}
              filterOptions={filterOptions}
            />
          </FiltersGroup>
        ))}
      </FiltersModal>

      <Show when={shouldShowClearButton}>
        <ClearFiltersButton onClick={resetFilters}>
          {t('Clear Filters')}
        </ClearFiltersButton>
      </Show>
    </InlineFilters>
  );
}
